// useProgress.tsx — shared progress state for the whole app.
//
// A small context wraps the ProgressStore so every screen reads the same
// journey state and re-renders when it changes. All mutations persist through
// the store (localStorage today, a database later).

import React from 'react';
import { Capacitor } from '@capacitor/core';
import {
  ProgressStore,
  type ProgressState,
  type AppRoute,
  type ProgrammeRoute,
  type DayCapacity,
  type SavedReflection,
  type ReviewComment,
} from '@/services/progress';
import { dayKey, dayKeyOf } from '@/services/dates';
import { REPROMPT_DAYS } from '@/services/researchCheckin';
import { recordSkip, shouldEscalate, REFLECTION_SOFT_NUDGE_UNDER_S, REFLECTION_SKIP_FLOOR_S } from '@/services/reflectionEngagement';
import {
  scheduleCalmPracticeReminder, cancelReminder, parseTime, REMINDER_IDS,
  scheduleDailyReturnReminders, cancelDailyReturnReminders,
} from '@/services/reminders';
import { GUIDE_MESSAGES, type GuideHook, type ActId } from '@/data/guideMessages';
import { TOOLS } from '@/data/toolkit';
import type { SyncBlob } from '@/services/sync';
import { latestAt, type FogsOpenMode } from '@/services/fogs';
import { trackEvent } from '@/services/metrics';
import { CONSENT_VERSIONS } from '@/config/consent';

interface ProgressApi {
  state: ProgressState;
  /**
   * In-memory, one-shot flag set ONLY by completeOnboarding(). Lets callers tell a
   * genuine onboarding completion (the user finished the flow) apart from a sync /
   * hydration flip of onboardingDone false→true for a returning account. Never
   * persisted or synced; resets on reload (a reload is not a completion).
   */
  onboardingJustCompleted: React.MutableRefObject<boolean>;
  isStepComplete: (stepId: string) => boolean;
  isModuleComplete: (moduleId: string) => boolean;
  markStepComplete: (stepId: string, opts?: { moduleId?: string; moduleStepIds?: string[] }) => void;
  setCurrentStep: (stepId: string) => void;
  getPosition: (stepId: string) => number;
  setPosition: (stepId: string, seconds: number) => void;
  saveReflection: (r: Omit<SavedReflection, 'savedAt'>) => void;
  getReflection: (stepId: string) => SavedReflection | undefined;
  /** Merge journal entries from an imported backup file (incoming wins on conflict). */
  importReflections: (entries: Record<string, SavedReflection>) => void;
  deleteReflection: (stepId: string) => void;
  isFavourite: (stepId: string) => boolean;
  toggleFavourite: (stepId: string) => void;
  addFavourite: (stepId: string) => void;
  dismissTestimonial: (id: string) => void;
  setMood: (moodId: string | null) => void;
  setLastRoute: (route: AppRoute) => void;
  /** Switch the active programme track (main ↔ self-compassion soft landing). */
  setActiveProgramme: (route: ProgrammeRoute) => void;
  /** Mark the onboarding tiles as worked through (unlocks the app). */
  completeOnboarding: () => void;
  /** First-session onboarding instrumentation — record the furthest step reached. */
  recordOnboardingStep: (n: number) => void;
  /** Record that the user showed up today (presence, never a streak). */
  recordPresence: () => void;
  /** Set today's capacity check-in answer — routes the day (capacity, not severity). */
  setDailyCapacity: (value: DayCapacity) => void;
  /** Mark a step as actively practised/tried (distinct from passively seen). */
  markPractised: (stepId: string) => void;
  /** Capture a Gollwitzer if-then implementation intention for a step. */
  saveIntention: (stepId: string, situation: string, action: string) => void;
  /** Pause the programme (no shame) with an optional reason. */
  pauseProgramme: (reason: string) => void;
  /** Resume the programme exactly where it was left. */
  resumeProgramme: () => void;
  /** Record that an act-boundary processing screen has been shown (so it appears once). */
  markActBoundarySeen: (actId: string) => void;
  /** Record a reflection's foreground dwell (seconds): updates the skip window and,
   *  if dwelt on long enough, marks the step practised. Returns the resulting flags. */
  recordReflectionDwell: (stepId: string, seconds: number) => void;
  /** "I'm doing these my own way" — durably silence reflection nudges. */
  setReflectionOwnWay: () => void;
  /** Set/clear the one-shot "key-skill mastery just happened" flag (drives H5). */
  setGuideMasteryDue: (due: boolean) => void;
  /** Set/clear the one-shot "over-pacing just escalated" flag (drives H7). */
  setGuideOverPacingDue: (due: boolean) => void;
  /** Set (or clear, with null) the main daily reminder time; schedules/cancels the
   *  rotating "come back" notifications at it. */
  setMorningAnchor: (time: string | null) => void;
  /** Set (or clear, with null) the optional daily Calm practice reminder time. */
  setCalmPracticeTime: (time: string | null) => void;
  /** Hide (true) or re-show (false) the Today-screen reminder nudge tile. */
  setReminderNudgeDismissed: (dismissed: boolean) => void;
  /** Mark the one-time demographics check-in as requested (set on first open). */
  markDemographicsCheckinDone: () => void;
  /** Record that the one-time "Your free trial has ended" sheet was shown (TRIAL_FULL_LOCK). */
  markTrialEndedSeen: () => void;
  /** Record that a research wellbeing check-in was OPENED. Deliberately does NOT
   *  snooze: opening is not completing, and the prompt must survive an abandoned
   *  form (see the note on markResearchCheckinOpened in the provider). */
  markResearchCheckinOpened: () => void;
  /** Dismiss the scheduled check-in prompt — gentle snooze for REPROMPT_DAYS. */
  snoozeResearchCheckin: () => void;
  /** Cache the backend-verified count of recorded check-in waves (from fetchMyProgress),
   *  and the latest wave's submission date — which anchors the next check-in interval. */
  setCompletedCheckinCount: (n: number, lastAt?: string | null) => void;
  /** Commit a shown guide-presence message — updates rotation, caps, impressions. */
  recordGuideImpression: (sel: { hook: GuideHook; clipId: string; quoteId?: string; act?: ActId }) => void;
  setDevMode: (on: boolean) => void;
  /** Dev: clear the guide kill-switch (reflectionOwnWay) + all guide caps so every
   *  guide tile can fire again. Used by the dev "reset guide state" button. */
  resetGuideState: () => void;
  /** Clinician Review Mode — show/hide the content comment affordances. */
  setReviewMode: (on: boolean) => void;
  /** Add a review note on a piece of content (returns nothing; ids are generated). */
  addReviewComment: (input: { targetId: string; kind: ReviewComment['kind']; text: string }) => void;
  /** Edit a review note's text. */
  updateReviewComment: (id: string, text: string) => void;
  /** Delete a review note. */
  deleteReviewComment: (id: string) => void;
  /** Store the quick-check themes (reveal keys incl. 'ex'); themes only, never answers. */
  setNeedsThemes: (themes: string[]) => void;
  /** Store the full-check result (flagged section keys); the later `at` wins. */
  setFogsResult: (flagged: string[], at?: string) => void;
  /** Stamp that the full check was opened from the app. */
  markFogsOpened: (mode?: Exclude<FogsOpenMode, 'failed'>) => void;
  /** Unused by UI: kept only for sync/storage compatibility. Hide the FOGS card until Act I is complete. */
  dismissFogsBeforeAct1: () => void;
  /** "Done it?" x: mark the full check done locally (no result came back). */
  dismissFogsDone: () => void;
  /** Unused by UI: kept only for sync/storage compatibility. Dismiss the one Act-I-complete FOGS reminder for good. */
  dismissFogsActReminder: () => void;
  /** Record that a tool's "unlocked" guide beat has been shown (announce once). */
  markToolAnnounced: (toolId: string) => void;
  /** Mark the one-time toolkit-encouragement beat as shown. */
  markToolkitIntroSeen: () => void;
  /** Dismiss the one-time "Inspire me" intro tile (module 1). */
  markInspireIntroSeen: () => void;
  /** Set (on chapter completion) or clear (on tap-away) the Today chapter-complete tile. */
  setChapterCelebration: (v: { moduleId: string; next: string } | null) => void;
  /** Count one ordinary session (called once per app launch) for the H11 cadence. */
  bumpEncouragementSession: () => void;
  /** Record that the H11 encouragement tile fired (stamps session + day). */
  markEncouragementShown: () => void;
  /** Record a Calm-Your-Body check-in (stamps the current session for the H6 cadence). */
  markCheckin: () => void;
  /** Record that a multi-day held step was practised today; returns the new count of
   *  distinct days practised (so the caller can decide whether the hold is met). */
  recordPracticeDay: (stepId: string) => number;
  /** Set the app-wide reading text scale (clamped 0.85–1.4). */
  setFontScale: (scale: number) => void;
  /** Record the research opt-in decision (gates surveys + lesson feedback), stamping
   *  the statement version it was made against. Logging to the server is the caller's
   *  job (services/consent.logConsent). */
  setResearchConsent: (decision: 'granted' | 'declined', version?: string) => void;
  /** Record the account-holder's type (onboarding, set once). Writing it to the
   *  profile — which is what gates the research PID — is the caller's job. */
  setUserType: (type: 'survivor' | 'curious' | 'staff' | 'other') => void;
  /** Record that the user accepted Terms & Privacy at the given version (clears the
   *  consent gate). Logging to the server is the caller's job. */
  acceptTerms: (version: string) => void;
  /** Mark a chapter's feedback card as answered/skipped (asked at most once per chapter). */
  markChapterFeedbackGiven: (chapterId: string) => void;
  /** Mark an act's wellbeing survey as answered/skipped (asked at most once). */
  markActSurveyGiven: (actId: string) => void;
  /** Record that the user started a Calm Your Body session (a moment of practice). */
  markCalmUsed: () => void;
  /** Record that the user opened/used a Toolkit tool. */
  markToolUsed: () => void;
  /** Apply a merged remote progress blob (sync pull) into local journey state.
   *  Only the operational fields in SyncBlob are overwritten; the blob is already
   *  unioned with local, so this never loses on-device progress. */
  applySyncBlob: (blob: SyncBlob) => void;
  /** Choose how the user journals. Records the choice (so the first-run note won't reappear). */
  setInAppJournaling: (on: boolean) => void;
  /** Offer the CARE reflection (after a Settle-My-Emotions session). */
  triggerCare: () => void;
  /** Dismiss the CARE prompt without doing it. */
  dismissCare: () => void;
  /** Record an empowering CARE action as a soft reminder on Today. */
  addCareAction: (text: string) => void;
  /** Mark a CARE action done (the user confirms they did it). */
  completeCareAction: (id: string) => void;
  reset: () => void;
}

const ProgressContext = React.createContext<ProgressApi | null>(null);

export function ProgressProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ProgressState>(() => ProgressStore.load());

  // Launch: the retired programme notification (4301) must not keep firing, and the
  // current reminders are re-armed (idempotent; also carries a migrated programme
  // reminder time over to the main daily reminder's notifications).
  React.useEffect(() => {
    void cancelReminder(REMINDER_IDS.programme);
    if (!Capacitor.isNativePlatform()) return;
    const main = parseTime(state.morningAnchorTime);
    if (main) void scheduleDailyReturnReminders(main.hour, main.minute);
    const calm = parseTime(state.calmPracticeTime);
    if (calm) void scheduleCalmPracticeReminder(calm.hour, calm.minute);
    // A pre-16-Sep build scheduled its own 4302 from the anchor; clear it unless Calm practice owns it.
    else void cancelReminder(REMINDER_IDS.calm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // See ProgressApi.onboardingJustCompleted — set only by completeOnboarding().
  const onboardingJustCompleted = React.useRef(false);

  const commit = React.useCallback((next: ProgressState) => {
    ProgressStore.save(next);
    setState(next);
  }, []);

  const update = React.useCallback(
    (fn: (prev: ProgressState) => ProgressState) => {
      setState((prev) => {
        const next = fn(prev);
        ProgressStore.save(next);
        return next;
      });
    },
    [],
  );

  const api = React.useMemo<ProgressApi>(
    () => ({
      state,
      onboardingJustCompleted,
      isStepComplete: (stepId) => state.completedSteps.includes(stepId),
      isModuleComplete: (moduleId) => state.completedModules.includes(moduleId),

      markStepComplete: (stepId, opts) => {
        // Anonymous completion telemetry — fired here (not inside the updater) and
        // only on a genuinely-new completion, so it's never double-counted.
        if (!state.completedSteps.includes(stepId)) trackEvent('complete', 'lesson', stepId);
        return update((prev) => {
          const already = prev.completedSteps.includes(stepId);
          const now = new Date().toISOString();
          const completedSteps = already ? prev.completedSteps : [...prev.completedSteps, stepId];
          // Stamp the first-completion date — used to spot a single-day chapter.
          const completedAt = already ? prev.completedAt : { ...prev.completedAt, [stepId]: now };

          // If every step in the module is now done, mark the module complete.
          // (The reactive single-day "rest" lock is retired in favour of the
          // proactive in-session spacing below.)
          let completedModules = prev.completedModules;
          if (opts?.moduleId && opts.moduleStepIds) {
            const allDone = opts.moduleStepIds.every((id) => completedSteps.includes(id));
            if (allDone && !completedModules.includes(opts.moduleId)) {
              completedModules = [...completedModules, opts.moduleId];
            }
          }

          // Record that the user showed up today (presence — never a streak).
          const today = dayKey();
          const presenceDays = prev.presenceDays.includes(today) ? prev.presenceDays : [...prev.presenceDays, today];

          // If a return (H2) guide message was shown today and the user is now
          // completing a step, flag it as having preceded a re-engagement.
          let guide = prev.guide;
          const h2Idx = guide.impressions.reduce(
            (acc, im, i) => (im.hook === 'H2_return' && !im.reengaged && dayKeyOf(im.at) === today ? i : acc),
            -1,
          );
          if (h2Idx >= 0) {
            const impressions = guide.impressions.slice();
            impressions[h2Idx] = { ...impressions[h2Idx], reengaged: true };
            guide = { ...guide, impressions };
          }

          return { ...prev, completedSteps, completedAt, completedModules, presenceDays, guide };
        });
      },

      setCurrentStep: (stepId) =>
        update((prev) => ({ ...prev, currentStepId: stepId })),

      getPosition: (stepId) => state.positions[stepId] ?? 0,
      setPosition: (stepId, seconds) =>
        update((prev) => ({
          ...prev,
          positions: { ...prev.positions, [stepId]: seconds },
        })),

      saveReflection: (r) =>
        update((prev) => ({
          ...prev,
          reflections: {
            ...prev.reflections,
            [r.stepId]: { ...r, savedAt: new Date().toISOString() },
          },
        })),
      getReflection: (stepId) => state.reflections[stepId],
      deleteReflection: (stepId) =>
        update((prev) => {
          const next = { ...prev.reflections };
          delete next[stepId];
          return { ...prev, reflections: next };
        }),

      isFavourite: (stepId) => state.favourites.includes(stepId),
      toggleFavourite: (stepId) => {
        if (!state.favourites.includes(stepId)) trackEvent('save', 'lesson', stepId); // only on add
        return update((prev) => ({
          ...prev,
          favourites: prev.favourites.includes(stepId)
            ? prev.favourites.filter((id) => id !== stepId)
            : [...prev.favourites, stepId],
        }));
      },
      addFavourite: (stepId) => {
        if (!state.favourites.includes(stepId)) trackEvent('save', 'lesson', stepId);
        return update((prev) =>
          prev.favourites.includes(stepId) ? prev : { ...prev, favourites: [...prev.favourites, stepId] },
        );
      },
      dismissTestimonial: (id) =>
        update((prev) =>
          prev.dismissedTestimonials.includes(id)
            ? prev
            : { ...prev, dismissedTestimonials: [...prev.dismissedTestimonials, id] },
        ),

      setMood: (moodId) => update((prev) => ({ ...prev, mood: moodId })),
      setLastRoute: (route) => update((prev) => ({ ...prev, lastRoute: route })),
      setActiveProgramme: (route) => update((prev) => ({ ...prev, activeProgramme: route })),
      completeOnboarding: () => {
        // Mark this as a genuine, user-driven completion so App can land the user in
        // their first lesson — WITHOUT misfiring when a returning account's remote
        // progress later hydrates onboardingDone false→true (that path never calls this).
        onboardingJustCompleted.current = true;
        update((prev) => ({
          ...prev,
          onboardingDone: true,
          onboarding: { ...prev.onboarding, completedAt: prev.onboarding.completedAt ?? new Date().toISOString() },
        }));
      },
      recordOnboardingStep: (n) =>
        update((prev) => (n <= prev.onboarding.reached ? prev : { ...prev, onboarding: { ...prev.onboarding, reached: n } })),
      recordPresence: () =>
        update((prev) => { const t = dayKey(); return prev.presenceDays.includes(t) ? prev : { ...prev, presenceDays: [...prev.presenceDays, t] }; }),
      setDailyCapacity: (value) =>
        update((prev) => {
          const t = dayKey();
          return {
            ...prev,
            dailyCapacity: { ...prev.dailyCapacity, [t]: value },
            presenceDays: prev.presenceDays.includes(t) ? prev.presenceDays : [...prev.presenceDays, t],
          };
        }),
      markPractised: (stepId) =>
        update((prev) => (prev.practisedSteps.includes(stepId) ? prev : { ...prev, practisedSteps: [...prev.practisedSteps, stepId] })),
      saveIntention: (stepId, situation, action) =>
        update((prev) => ({ ...prev, intentions: { ...prev.intentions, [stepId]: { situation, action, createdAt: new Date().toISOString() } } })),
      pauseProgramme: (reason) =>
        update((prev) => ({ ...prev, paused: { since: new Date().toISOString(), reason } })),
      resumeProgramme: () => update((prev) => ({ ...prev, paused: null })),
      markActBoundarySeen: (actId) =>
        update((prev) => (prev.actBoundariesSeen.includes(actId) ? prev : { ...prev, actBoundariesSeen: [...prev.actBoundariesSeen, actId] })),
      recordReflectionDwell: (stepId, seconds) =>
        update((prev) => {
          const underFloor = seconds < REFLECTION_SKIP_FLOOR_S;
          const practised = seconds >= REFLECTION_SOFT_NUDGE_UNDER_S;
          const nextSkips = recordSkip(prev.reflectionSkips, underFloor);
          // H7 over-pacing fires on the ESCALATION EDGE only (the run just crossed
          // into "3 of the last 5 rushed"), so it's a once-per-episode signal, not a
          // cold-start one. Preserve an already-set, not-yet-consumed flag.
          const overPacingDue =
            prev.guideOverPacingDue || (shouldEscalate(nextSkips) && !shouldEscalate(prev.reflectionSkips));
          return {
            ...prev,
            reflectionSkips: nextSkips,
            guideOverPacingDue: overPacingDue,
            practisedSteps:
              practised && !prev.practisedSteps.includes(stepId)
                ? [...prev.practisedSteps, stepId]
                : prev.practisedSteps,
          };
        }),
      setReflectionOwnWay: () => update((prev) => ({ ...prev, reflectionOwnWay: true })),
      setGuideMasteryDue: (due) => update((prev) => (prev.guideMasteryDue === due ? prev : { ...prev, guideMasteryDue: due })),
      setGuideOverPacingDue: (due) => update((prev) => (prev.guideOverPacingDue === due ? prev : { ...prev, guideOverPacingDue: due })),
      setMorningAnchor: (time) =>
        update((prev) => {
          const hm = time ? parseTime(time) : null;
          // The daily anchor fires the RETURN reminder: seven weekly notifications,
          // one per weekday, so the words change through the week instead of the same
          // sentence arriving every morning forever.
          if (hm) void scheduleDailyReturnReminders(hm.hour, hm.minute);
          else void cancelDailyReturnReminders();
          return { ...prev, morningAnchorTime: hm ? (time as string) : null };
        }),
      setCalmPracticeTime: (time) =>
        update((prev) => {
          const hm = time ? parseTime(time) : null;
          if (hm) void scheduleCalmPracticeReminder(hm.hour, hm.minute);
          else void cancelReminder(REMINDER_IDS.calm);
          return { ...prev, calmPracticeTime: hm ? (time as string) : null };
        }),
      setReminderNudgeDismissed: (dismissed) =>
        update((prev) => ({ ...prev, reminderNudgeDismissed: dismissed })),
      markTrialEndedSeen: () =>
        update((prev) => (prev.trialEndedSeenAt ? prev : { ...prev, trialEndedSeenAt: new Date().toISOString() })),
      markDemographicsCheckinDone: () =>
        update((prev) => (prev.demographicsCheckinDone ? prev : { ...prev, demographicsCheckinDone: true })),
      // OPENING a check-in changes nothing. It used to snooze the prompt for
      // REPROMPT_DAYS, which meant tapping "Complete a check-in" and then closing the
      // form (or the browser) buried the reminder for three days — the wave could be
      // missed entirely and the user saw no sign of it. Completion is backend-verified
      // via completedCheckinCount, so the honest rule is: the prompt stands until the
      // server holds a submission for this wave. An explicit "not now" still snoozes
      // (snoozeResearchCheckin) — that is the user's choice, not an accident of tapping.
      // Never stamp lastResearchCheckinAt here either: that field is the last actual
      // SUBMISSION date and it anchors the whole schedule.
      markResearchCheckinOpened: () => {},
      snoozeResearchCheckin: () =>
        update((prev) => ({ ...prev, checkinSnoozeUntil: new Date(Date.now() + REPROMPT_DAYS * 86_400_000).toISOString() })),
      setCompletedCheckinCount: (n, lastAt) =>
        update((prev) => {
          const sameCount = prev.completedCheckinCount === n;
          const sameAt = !lastAt || prev.lastResearchCheckinAt === lastAt;
          if (sameCount && sameAt) return prev;
          return { ...prev, completedCheckinCount: n, ...(lastAt ? { lastResearchCheckinAt: lastAt } : {}) };
        }),
      recordGuideImpression: (sel) =>
        update((prev) => {
          const now = new Date().toISOString();
          const today = dayKey();
          const g = prev.guide;
          const recent = sel.quoteId ? [...(g.recentQuoteIds[sel.hook] ?? []), sel.quoteId].slice(-8) : g.recentQuoteIds[sel.hook] ?? [];
          return {
            ...prev,
            guide: {
              ...g,
              recentQuoteIds: { ...g.recentQuoteIds, [sel.hook]: recent },
              seen: { ...g.seen, [sel.hook]: GUIDE_MESSAGES[sel.hook].freq === 'light' ? today : true },
              boundariesShown: sel.act && !g.boundariesShown.includes(sel.act) ? [...g.boundariesShown, sel.act] : g.boundariesShown,
              lastReturnHandled: sel.hook === 'H2_return' ? today : g.lastReturnHandled,
              lastGuideMessageAt: now,
              impressions: [...g.impressions, { hook: sel.hook, clipId: sel.clipId, quoteId: sel.quoteId, at: now }].slice(-200),
            },
          };
        }),
      setDevMode: (on) => update((prev) => ({ ...prev, devMode: on })),
      resetGuideState: () =>
        update((prev) => ({
          ...prev,
          reflectionOwnWay: false,
          reflectionSkips: [],
          guideMasteryDue: false,
          guideOverPacingDue: false,
          toolkitAnnounced: [],
          toolkitIntroSeen: false,
          inspireIntroSeen: false,
          guide: { recentQuoteIds: {}, seen: {}, boundariesShown: [], lastReturnHandled: null, lastGuideMessageAt: null, impressions: [] },
        })),
      setReviewMode: (on) => update((prev) => ({ ...prev, reviewMode: on })),
      addReviewComment: ({ targetId, kind, text }) =>
        update((prev) => {
          const t = text.trim();
          if (!t) return prev;
          const now = new Date().toISOString();
          const comment: ReviewComment = {
            id: globalThis.crypto?.randomUUID?.() ?? `rev-${now}-${prev.reviewComments.length}`,
            targetId,
            kind,
            text: t,
            createdAt: now,
            updatedAt: now,
          };
          return { ...prev, reviewComments: [...prev.reviewComments, comment] };
        }),
      updateReviewComment: (id, text) =>
        update((prev) => {
          const t = text.trim();
          return {
            ...prev,
            reviewComments: prev.reviewComments.map((c) =>
              c.id === id ? { ...c, text: t, updatedAt: new Date().toISOString() } : c,
            ),
          };
        }),
      deleteReviewComment: (id) =>
        update((prev) => ({ ...prev, reviewComments: prev.reviewComments.filter((c) => c.id !== id) })),
      markToolAnnounced: (toolId) =>
        update((prev) => (prev.toolkitAnnounced.includes(toolId) ? prev : { ...prev, toolkitAnnounced: [...prev.toolkitAnnounced, toolId] })),
      markToolkitIntroSeen: () =>
        update((prev) => (prev.toolkitIntroSeen ? prev : { ...prev, toolkitIntroSeen: true })),
      markInspireIntroSeen: () =>
        update((prev) => (prev.inspireIntroSeen ? prev : { ...prev, inspireIntroSeen: true })),
      setChapterCelebration: (v) => update((prev) => ({ ...prev, chapterCelebration: v })),
      bumpEncouragementSession: () =>
        update((prev) => ({ ...prev, encouragement: { ...prev.encouragement, sessions: prev.encouragement.sessions + 1 } })),
      markEncouragementShown: () =>
        update((prev) => ({ ...prev, encouragement: { ...prev.encouragement, lastFiredSession: prev.encouragement.sessions, lastDay: dayKey() } })),
      markCheckin: () =>
        update((prev) => ({ ...prev, lastCheckinSession: prev.encouragement.sessions })),
      recordPracticeDay: (stepId) => {
        const today = dayKey();
        const prev = ProgressStore.load();
        const existing = prev.practiceDays[stepId] ?? [];
        const days = existing.includes(today) ? existing : [...existing, today];
        commit({ ...prev, practiceDays: { ...prev.practiceDays, [stepId]: days } });
        return days.length;
      },
      setInAppJournaling: (on) =>
        update((prev) => ({ ...prev, inAppJournaling: on, journalingChoiceMade: true })),
      importReflections: (entries) =>
        update((prev) => ({ ...prev, reflections: { ...prev.reflections, ...entries } })),
      setFontScale: (scale) =>
        update((prev) => ({ ...prev, fontScale: Math.min(1.4, Math.max(0.85, Math.round(scale * 100) / 100)) })),
      setResearchConsent: (decision, version = CONSENT_VERSIONS.research) =>
        update((prev) => ({ ...prev, researchConsent: decision, researchConsentVersion: version })),
      setUserType: (type) => update((prev) => (prev.userType ? prev : { ...prev, userType: type })),
      acceptTerms: (version) => update((prev) => ({ ...prev, termsAcceptedVersion: version })),
      markChapterFeedbackGiven: (chapterId) =>
        update((prev) => (prev.chapterFeedbackGiven.includes(chapterId) ? prev : { ...prev, chapterFeedbackGiven: [...prev.chapterFeedbackGiven, chapterId] })),
      markActSurveyGiven: (actId) =>
        update((prev) => (prev.actSurveysGiven.includes(actId) ? prev : { ...prev, actSurveysGiven: [...prev.actSurveysGiven, actId] })),
      markCalmUsed: () => update((prev) => ({ ...prev, calmSessionsCount: (prev.calmSessionsCount ?? 0) + 1 })),
      markToolUsed: () => update((prev) => ({ ...prev, toolUsesCount: (prev.toolUsesCount ?? 0) + 1 })),
      setNeedsThemes: (themes) => update((prev) => ({ ...prev, needsThemes: { themes, at: new Date().toISOString() } })),
      setFogsResult: (flagged, at) =>
        update((prev) => {
          if (at === undefined) {
            // A wave with no timestamp: never re-stamp "now" on every refresh. Same flagged set
            // as already stored = nothing to do; otherwise record it once.
            const same = prev.fogsResult && prev.fogsResult.flagged.length === flagged.length && flagged.every((k) => prev.fogsResult!.flagged.includes(k));
            return same ? prev : { ...prev, fogsResult: { flagged, at: new Date().toISOString() } };
          }
          return prev.fogsResult && Date.parse(prev.fogsResult.at) >= Date.parse(at) ? prev : { ...prev, fogsResult: { flagged, at } };
        }),
      markFogsOpened: (mode) => update((prev) => ({ ...prev, fogsOpenedAt: new Date().toISOString(), fogsOpenedMode: mode ?? prev.fogsOpenedMode })),
      dismissFogsBeforeAct1: () => update((prev) => (prev.fogsDismissedBeforeAct1 ? prev : { ...prev, fogsDismissedBeforeAct1: true })),
      dismissFogsDone: () => update((prev) => (prev.fogsDoneLocal ? prev : { ...prev, fogsDoneLocal: true })),
      dismissFogsActReminder: () => update((prev) => (prev.fogsActReminderDone ? prev : { ...prev, fogsActReminderDone: true })),
      applySyncBlob: (blob) =>
        update((prev) => {
          // Reconcile the toolkit-announce set on restore: a returning user (whose
          // completedSteps come back from the server but whose local toolkitAnnounced is
          // empty) must NOT be flooded with old unlock notices. Treat every tool whose
          // unlock lesson is already complete as already-announced; only tools unlocked
          // in THIS session (after the restore) fire H9. Same for the one-time H10 intro.
          const alreadyUnlocked = TOOLS.filter(
            (t) => t.unlockLessonId && blob.completedSteps.includes(t.unlockLessonId),
          ).map((t) => t.id);
          return {
            ...prev,
            completedSteps: blob.completedSteps,
            completedModules: blob.completedModules,
            completedAt: blob.completedAt,
            currentStepId: blob.currentStepId,
            positions: blob.positions,
            favourites: blob.favourites,
            practisedSteps: blob.practisedSteps,
            practiceDays: blob.practiceDays,
            presenceDays: blob.presenceDays,
            actBoundariesSeen: blob.actBoundariesSeen,
            activeProgramme: blob.activeProgramme,
            onboardingDone: blob.onboardingDone,
            // Moments of practice — never lower what's already on the device.
            calmSessionsCount: blob.calmSessionsCount ?? prev.calmSessionsCount,
            toolUsesCount: blob.toolUsesCount ?? prev.toolUsesCount,
            // Latest `at` wins, so a pull that was in flight cannot overwrite a newer local result.
            needsThemes: latestAt(prev.needsThemes, blob.needsThemes),
            fogsResult: latestAt(prev.fogsResult, blob.fogsResult),
            ...(blob.fogsOpenedAt && (!prev.fogsOpenedAt || Date.parse(blob.fogsOpenedAt) > Date.parse(prev.fogsOpenedAt))
              ? { fogsOpenedAt: blob.fogsOpenedAt, fogsOpenedMode: blob.fogsOpenedMode ?? null }
              : {}),
            fogsDismissedBeforeAct1: !!blob.fogsDismissedBeforeAct1 || prev.fogsDismissedBeforeAct1,
            fogsActReminderDone: !!blob.fogsActReminderDone || prev.fogsActReminderDone,
            fogsDoneLocal: !!blob.fogsDoneLocal || prev.fogsDoneLocal,
            toolkitAnnounced: Array.from(new Set([...prev.toolkitAnnounced, ...alreadyUnlocked])),
            toolkitIntroSeen: prev.toolkitIntroSeen || alreadyUnlocked.length > 0,
          };
        }),
      triggerCare: () => update((prev) => (prev.careTriggered ? prev : { ...prev, careTriggered: true })),
      dismissCare: () => update((prev) => ({ ...prev, careTriggered: false })),
      addCareAction: (text) =>
        update((prev) => {
          const t = text.trim();
          if (!t) return { ...prev, careTriggered: false };
          const action = {
            id: globalThis.crypto?.randomUUID?.() ?? `care-${new Date().toISOString()}`,
            text: t,
            createdAt: new Date().toISOString(),
            doneAt: null,
          };
          return { ...prev, careTriggered: false, careActions: [...prev.careActions, action] };
        }),
      completeCareAction: (id) =>
        update((prev) => ({
          ...prev,
          careActions: prev.careActions.map((a) =>
            a.id === id ? { ...a, doneAt: new Date().toISOString() } : a,
          ),
        })),
      reset: () => commit(ProgressStore.reset()),
    }),
    [state, update, commit],
  );

  return <ProgressContext.Provider value={api}>{children}</ProgressContext.Provider>;
}

export function useProgress(): ProgressApi {
  const ctx = React.useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used within a ProgressProvider');
  return ctx;
}
